import DateRangeSelectorSelect from './DateRangeSelectorSelect';
import * as M from 'moment/moment';
import { extendMoment } from 'moment-range';
import { Moment } from 'moment';
import { DashboardDateRangeId } from '../../util/types';
import $ from 'jquery';
import { DASHBOARDS_BACKEND_URL } from '../util/config';
import { toast } from 'react-toastify';
import { Modal, Skeleton } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { isDataAvailableForDateRange } from './dateRangeSelectorUtil';
import DateRangeSelectorPicker from './DateRangeSelectorPicker';
import { DashboardDateRange } from '../../util/DashboardDateRange';

const moment = extendMoment(M);

const DEFAULT_MAX_DATE_RANGE = 7;
const DATE_FORMAT = 'YYYY-MM-DD';
const MONTH_FORMAT = 'YYYYMM';
const HISTORIC_DATA_AVAILABLE_MONTHS_CA = ['202108'];
const HISTORIC_DATA_AVAILABLE_MONTHS_CT = ['202108'];
const HISTORIC_DATA_AVAILABLE_MONTHS_GA = ['202108'];
const HISTORIC_DATA_AVAILABLE_MONTHS_IN = ['201907', '202001', '202006', '202007', '202008', '202009', '202011', '202012'];
const HISTORIC_DATA_AVAILABLE_MONTHS_MN = ['202108'];
const HISTORIC_DATA_AVAILABLE_MONTHS_NC = ['202008', '202108'];
const HISTORIC_DATA_AVAILABLE_MONTHS_OH = ['202008', '202108'];
const HISTORIC_DATA_AVAILABLE_MONTHS_PA = ['202008', '202104', '202105', '202106', '202107', '202108'];
const HISTORIC_DATA_AVAILABLE_MONTHS_TX = ['202008', '202108'];
const HISTORIC_DATA_AVAILABLE_MONTHS_UT = ['202108'];
const HISTORIC_DATA_AVAILABLE_MONTHS_WI = ['202108'];

const DEFAULT_RANGE_PICKER_QUICK_RANGES: Record<string, [Moment, Moment]> = {
  'Today': [moment(), moment()],
  '3 Days': [moment().subtract(2, 'days'), moment()],
  'This Week': [moment().startOf('week'), moment()],
  'Last Week': [moment().subtract(7, 'days').startOf('week'), moment().subtract(7, 'days').endOf('week')],
  'August 2021': [moment('2021-08-01'), moment('2021-08-02')],
  'August 2020': [moment('2020-08-01'), moment('2020-08-02')],
};

type DateRangeSelectorProps = {
  style?: object;
  routeId: number;
  dateRangeId: DashboardDateRangeId;
  organizationName?: string;
  onChange: (dateRange: DashboardDateRange, index?: number) => void;
  quickRanges?: Record<string, [Moment, Moment]>;
  maxDateRange?: number;
  onNewDateRanges?: (dateRanges: DashboardDateRange[]) => void;
  allDateRanges?: DashboardDateRange[];
  index?: number;
};

/**
 * Given props, provides a DashboardDateRange
 * This will cause duplicate request for date ranges when there are multiple of these on one page, but idrc
 * @param props
 * @constructor
 */
const DateRangeSelector = (props: DateRangeSelectorProps) => {

  const dateRangeIdRef = useRef<DashboardDateRangeId>(props.dateRangeId);

  const [
    dataAvailableDatesCA,
    dataAvailableDatesCT,
    dataAvailableDatesGA,
    dataAvailableDatesIN,
    dataAvailableDatesMN,
    dataAvailableDatesNC,
    dataAvailableDatesOH,
    dataAvailableDatesPA,
    dataAvailableDatesTX,
    dataAvailableDatesUT,
    dataAvailableDatesWI,
  ] = [
    HISTORIC_DATA_AVAILABLE_MONTHS_CA,
    HISTORIC_DATA_AVAILABLE_MONTHS_CT,
    HISTORIC_DATA_AVAILABLE_MONTHS_GA,
    HISTORIC_DATA_AVAILABLE_MONTHS_IN,
    HISTORIC_DATA_AVAILABLE_MONTHS_MN,
    HISTORIC_DATA_AVAILABLE_MONTHS_NC,
    HISTORIC_DATA_AVAILABLE_MONTHS_OH,
    HISTORIC_DATA_AVAILABLE_MONTHS_PA,
    HISTORIC_DATA_AVAILABLE_MONTHS_TX,
    HISTORIC_DATA_AVAILABLE_MONTHS_UT,
    HISTORIC_DATA_AVAILABLE_MONTHS_WI,
  ].map((historic) => historic
    .map(m => moment.range(
      moment(m, MONTH_FORMAT).startOf('month'),
      moment(m, MONTH_FORMAT).endOf('month'),
    )),
  );

  dataAvailableDatesIN.push(moment.range(moment('2021-03-24'), moment('2021-10-31').endOf('day')));
  // adjust dates available for Indiana to end on June 2, 2023 when Wejo ended - test
  dataAvailableDatesIN.push(moment.range(moment('2022-01-01'), moment('2023-06-02').endOf('day')));

  const dataAvailableDatesCANew = [moment.range(moment('2025-11-09'), moment('2025-11-16').endOf('day'))];
  const dataAvailableDatesINNew = [moment.range(moment('2024-05-31'), moment().endOf('day'))];

  const [dateRanges, setDateRanges] = useState<DashboardDateRange[]>(props.allDateRanges || []);
  const [isLoadingDateRanges, setIsLoadingDateRanges] = useState<boolean>(false);

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

  useEffect(() => {
    dateRangeIdRef.current = props.dateRangeId;
  }, [props.dateRangeId]);

  useEffect(() => {
    if (!isLoadingDateRanges) getDateRanges();
  }, [props.dateRangeId]);

  useEffect(() => {
    if (!props.allDateRanges) return;
    setDateRanges(props.allDateRanges);
    setIsLoadingDateRanges(false);
  }, [props.allDateRanges]);

  useEffect(() => {
    // If the date range is 1, there is no route selected, and the date ranges have loaded, and there aren't any available dynamic date ranges,
    // get a new date range with the first two days that data is available for.
    const dataAvailabilityDates = getDataAvailabilityRange();
    if (props.dateRangeId === 1 && !props.routeId && dateRanges.length &&
      !dateRanges.filter(d => !!d.name).map(d => isDataAvailableForDateRange(d, dataAvailabilityDates)).filter(Boolean).length) {
      // TODO Un-hard-code this
      // Grab the first two days of the first date range
      createDateRange(dataAvailabilityDates[0].start.format(DATE_FORMAT), dataAvailabilityDates[0].start.add(1, 'day').format(DATE_FORMAT));
    }
  }, [dateRanges]);

  const getDataAvailabilityRange = () => {
    console.log(props.organizationName);
    switch (props.organizationName) {
      case 'Caltrans':
        return dataAvailableDatesCA;
      case 'Caltrans-New':
        return dataAvailableDatesCANew;
      case 'CTDOT':
        return dataAvailableDatesCT;
      case 'GDOT':
        return dataAvailableDatesGA;
      case 'INDOT-New':
        return dataAvailableDatesINNew;
      case 'JTRP-New':
        return dataAvailableDatesINNew;
      case 'MnDOT':
        return dataAvailableDatesMN;
      case 'NCDOT':
        return dataAvailableDatesNC;
      case 'ODOT':
        return dataAvailableDatesOH;
      case 'PennDOT':
        return dataAvailableDatesPA;
      case 'TxDOT':
        return dataAvailableDatesTX;
      case 'UDOT':
        return dataAvailableDatesUT;
      case 'WisDOT':
        return dataAvailableDatesWI;
      default:
        return dataAvailableDatesIN;
    }
  };

  const getDateRanges = () => {
    if (sessionStorage.getItem('sessionId')) {
      $.ajax(`${DASHBOARDS_BACKEND_URL}date_range/${new Date().getTimezoneOffset()}`, {
        method: 'GET',
        beforeSend: xhr => {
          xhr.setRequestHeader('Authorization', `Bearer ${sessionStorage.getItem('sessionId')}`);
          setIsLoadingDateRanges(true);
        },
        success: data => {
          // This kind of stuff makes me hate typescript
          const refreshedDateRanges: DashboardDateRange[] = data.map((dr: {
            date_range_id: DashboardDateRangeId
            date_range_name: string,
            start_date: string,
            end_date: string,
            parsed_start_date: string,
            parsed_end_date: string
          }) => {
            return new DashboardDateRange(
              dr.date_range_id,
              dr.date_range_name,
              dr.start_date,
              dr.end_date,
              dr.parsed_start_date,
              dr.parsed_end_date);
          });
          setDateRanges(refreshedDateRanges);
          if (props.onNewDateRanges) props.onNewDateRanges(refreshedDateRanges);

          props.onChange(refreshedDateRanges.filter(d => d.dateRangeId === dateRangeIdRef.current)[0], props.index);

          setIsModalVisible(false);
          setConfirmLoading(false);
          setIsLoadingDateRanges(false);
        },
        error: err => {
          toast.error('Error getting date ranges!');
          console.log(err);
          setIsModalVisible(false);
          setConfirmLoading(false);
          setIsLoadingDateRanges(false);
        },
      });
    }
  };

  /**
   * Create a new date range in the backend, and set the resultant ID to the currently selected date range.
   * @param start YYYY-MM-DD start date
   * @param end YYYY-MM-DD end date
   */
  const createDateRange = (start: string | undefined, end: string | undefined) => {
    if (!start || !end) return;
    if (sessionStorage.getItem('sessionId')) {
      $.ajax(`${DASHBOARDS_BACKEND_URL}date_range/`, {
        method: 'POST',
        data: JSON.stringify({ 'start_date': start, 'end_date': end }),
        contentType: 'application/json',
        dataType: 'json',
        beforeSend: xhr => {
          xhr.setRequestHeader('Authorization', `Bearer ${sessionStorage.getItem('sessionId')}`);
          setConfirmLoading(true);
        },
        success: data => {
          props.onChange(new DashboardDateRange(data[0].date_range_id), props.index);
        },
        error: err => {
          // That date range already exists
          if (err.status === 409) {
            props.onChange(new DashboardDateRange(err.responseJSON[0].date_range_id), props.index);
          } else {
            console.log(err);
            toast.error('Error creating date range!');
            setIsModalVisible(false);
            // setDateRangeModalVisible(false);
            setConfirmLoading(false);
            getDateRanges();
          }
        },

      });
    }
  };

  const onDateRangeSelectorSelectChange = (dateRangeId: DashboardDateRangeId) => {
    const dr = dateRanges.filter(d => d.dateRangeId === dateRangeId)[0];
    props.onChange(dr, props.index);
  };

  const onDateRangeSelectorPickerChange = (dates: string[]) => {
    const [startDate, endDate] = dates;
    createDateRange(startDate, endDate);
  };

  const setModalVisible = () => setIsModalVisible(true);
  const setModalInvisible = () => {
    setIsModalVisible(false);
  };

  if (!dateRanges.length) {
    return (
      <div style={{ display: 'flex' }}>
        <div style={props.style}>
          <Skeleton active={true} paragraph={{ rows: 1 }} title={false}/>
        </div>
      </div>
    );
  } else {
    return (
      <>
        <DateRangeSelectorSelect
          style={props.style}
          onChange={onDateRangeSelectorSelectChange}
          maxDaysSelected={props.maxDateRange || DEFAULT_MAX_DATE_RANGE}
          dataAvailableDates={getDataAvailabilityRange()}
          createStaticRange={setModalVisible}
          dateFormat={DATE_FORMAT}
          dateRangeId={props.dateRangeId}
          dateRanges={dateRanges}/>
        <Modal
          title="Select Static Date Range"
          visible={isModalVisible}
          confirmLoading={confirmLoading}
          okButtonProps={{ onClick: setModalInvisible }}
          onCancel={setModalInvisible}
          width={300}
        >
          <DateRangeSelectorPicker
            quickRanges={props.quickRanges || DEFAULT_RANGE_PICKER_QUICK_RANGES}
            onChange={onDateRangeSelectorPickerChange}
            maxDaysSelected={props.maxDateRange || DEFAULT_MAX_DATE_RANGE}
            dateFormat={DATE_FORMAT}
            dataAvailableDates={getDataAvailabilityRange()}/>
        </Modal>
      </>
    );
  }
};

export default DateRangeSelector;
